import { useCallback } from "react";
import {
  Account,
  AppField,
  Application,
  BaseRecord,
  LinkedRecordFieldParams,
  Member,
  Preferences,
  Solution,
} from "@smartsuite/types";
import {
  BulkUpdateFieldValueEvent,
  ConfirmBulkPasteCallbackParams,
  ExpandViewModalCallbackParams,
  FieldListingClick,
  SaveRecordEvent,
} from "@smartsuite/gridview/lib/components/GridView/GridView.types";
import { ConfirmDeleteCellsCallbackParams } from "@smartsuite/gridview/lib/components/GridView/components/GridViewCellRangeSelection/useCleanGridCellRange";
import { BulkPasteParams } from "@smartsuite/gridview/lib/types";
import { RecordOrderingData } from "@smartsuite/react-ui/lib/hooks/useRecordDragging/helpers";
import { noop } from "rxjs";

export interface LinkedRecordTableRecordActionsProps {
  account?: Account;
  linkedRecordField: AppField<LinkedRecordFieldParams>;
  application: Application | null;
  solution: Solution | null;
  applicationRecords: BaseRecord[];
  userPreferences: Preferences;
  currentMember: Member | null;
  currentRecord: BaseRecord;
  showAllOnRecordsSave: boolean;
  changeRecordsOrdering: (records: BaseRecord[]) => void;
  onCreateRecord: (record: BaseRecord) => void;
  onRecordRemove: (recordId: string) => void;
  onShowAll?: () => void;
}

export interface LinkedRecordTableRecordActionsResult {
  isRecordsLimitExceeded: () => boolean;
  getAvailableRecordsToCreate: () => number | null;
  onOpenRecordsLimitExceededModal: () => void;
  onOpenFieldListingModal: (addNextTo: string, fieldListingClick: FieldListingClick) => void;
  openExpandViewModal: (params: ExpandViewModalCallbackParams) => Promise<boolean | null>;
  confirmBulkPaste: (params: ConfirmBulkPasteCallbackParams) => Promise<boolean | null>;
  confirmCellsDelete: (params: ConfirmDeleteCellsCallbackParams) => Promise<boolean>;
  onSaveRecord: (event: SaveRecordEvent<BaseRecord>) => void;
  onDeleteRecord: (record: BaseRecord) => void;
  onUnlinkRecord: (record: BaseRecord) => void;
  onReorderRecords: (data: RecordOrderingData) => void;
  onBulkPaste: (params: BulkPasteParams) => void;
  onBulkUpdateFieldValue: (event: BulkUpdateFieldValueEvent) => void;
  onCopyRecordLink: (record: BaseRecord) => void;
  onPrintRecord: (record: BaseRecord) => void;
  onExportRecordToPdf: (record: BaseRecord) => void;
  onExportRecordToExcel: (record: BaseRecord) => void;
  onExportRecordToCsv: (record: BaseRecord) => void;
  onExportRecordToGoogleSheets: (record: BaseRecord) => void;
  onShareRecordByEmail: (record: BaseRecord) => void;
}

export const useLinkedRecordTableRecordActions = ({
  onRecordRemove,
}: LinkedRecordTableRecordActionsProps): LinkedRecordTableRecordActionsResult => {
  const onUnlinkRecord = useCallback(
    (record: BaseRecord) => {
      onRecordRemove(String(record.id));
    },
    [onRecordRemove]
  );

  const onSaveRecord = useCallback((event: SaveRecordEvent<BaseRecord>) => {
    event.onSave(event.record as BaseRecord);
  }, []);

  const onReorderRecords = useCallback((_data: RecordOrderingData) => undefined, []);

  return {
    isRecordsLimitExceeded: () => false,
    getAvailableRecordsToCreate: () => null,
    onOpenRecordsLimitExceededModal: noop,
    onOpenFieldListingModal: noop,
    openExpandViewModal: async () => await Promise.resolve(true),
    confirmBulkPaste: async () => await Promise.resolve(false),
    confirmCellsDelete: async () => await Promise.resolve(false),
    onSaveRecord,
    onDeleteRecord: noop,
    onUnlinkRecord,
    onReorderRecords,
    onBulkPaste: noop,
    onBulkUpdateFieldValue: noop,
    onCopyRecordLink: noop,
    onPrintRecord: noop,
    onExportRecordToPdf: noop,
    onExportRecordToExcel: noop,
    onExportRecordToCsv: noop,
    onExportRecordToGoogleSheets: noop,
    onShareRecordByEmail: noop,
  };
};
